import { memo, useContext, useEffect, useState } from 'react';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Accordion from '@mui/material/Accordion';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { capitalizeFirstLetter } from '../../utils/capitalizeFirstLetter';
import Typography from '@mui/material/Typography';
import { checkIsObject } from '../../utils/helperFunctions';
import {
  Box,
  IconButton,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import { FormContext } from './FormContext';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';

const TooltipWrapper = ({ title, children }) => {
  if (title) {
    return <Tooltip title={title}>{children} </Tooltip>;
  }

  return children;
};

const SortableRow = ({ field, parentPath, id }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    zIndex: isDragging ? 9999 : 'auto',
    position: isDragging ? 'relative' : 'static',
  };

  return (
    <TableRow
      ref={setNodeRef}
      style={style}
      {...attributes}
      sx={{
        cursor: 'auto',
      }}
    >
      <TableCell
        {...listeners}
        sx={{
          width: '40px',
          cursor: 'grab',
          '&:active': {
            cursor: 'grabbing',
          },
        }}
      >
        <IconButton size="small" sx={{ cursor: 'inherit !important' }}>
          <DragIndicatorIcon fontSize="small" />
        </IconButton>
      </TableCell>

      <FieldSettings field={field} parentPath={parentPath} />
    </TableRow>
  );
};

const FieldSettings = memo(({ field, parentPath }) => {
  const formContext = useContext(FormContext);

  return (
    <>
      <TableCell>{field.label}</TableCell>
      <TableCell>
        <TooltipWrapper title={field.unchangeable && 'NERIS mandatory'}>
          <Switch
            size="small"
            disabled={field.unchangeable}
            checked={field.active}
            onChange={(_, value) => {
              formContext.onChange(`${parentPath}.active`, value);
            }}
          />
        </TooltipWrapper>
      </TableCell>
      <TableCell>
        <Switch
          size="small"
          checked={field.required}
          onChange={(_, value) => {
            formContext.onChange(`${parentPath}.required`, value);
          }}
        />
      </TableCell>
    </>
  );
});

const FieldsList = ({ fieldList, parentPath }) => {
  const formContext = useContext(FormContext);
  const [items, setItems] = useState([]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Initialize items when fieldList changes
  useEffect(() => {
    if (checkIsObject(fieldList)) {
      const sortedItems = Object.entries(fieldList)
        .sort(
          ([, fieldA], [, fieldB]) => (fieldA.order || 0) - (fieldB.order || 0)
        )
        .map(([fieldName, field]) => ({
          id: fieldName,
          fieldName,
          field,
        }));
      setItems(sortedItems);
    }
  }, [fieldList]);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active?.id && over?.id && active.id !== over.id) {
      setItems((items) => {
        const oldIndex = items.findIndex((item) => item?.id === active.id);
        const newIndex = items.findIndex((item) => item?.id === over.id);

        if (oldIndex === -1 || newIndex === -1) return items;

        const newItems = arrayMove(items, oldIndex, newIndex);

        // Update order in form context
        formContext.onChange(
          newItems
            .filter((item) => item?.fieldName)
            .map((item, index) => [
              `${parentPath}.${item.fieldName}.order`,
              index,
            ])
        );

        // Update local order
        return newItems;
      });
    }
  };

  return (
    <Paper>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <Table size="small">
          <TableHead>
            <TableRow
              sx={{
                backgroundColor: '#f5f5f5 !important',
              }}
            >
              <TableCell sx={{ width: '40px' }}></TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Field in Form</TableCell>
              <TableCell>Department Required</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <SortableContext
              items={items.filter((item) => item?.id).map((item) => item.id)}
              strategy={verticalListSortingStrategy}
            >
              {items
                .filter((item) => item?.id && item?.field && item?.fieldName)
                .map((item) => (
                  <SortableRow
                    key={item.id}
                    id={item.id}
                    field={item.field}
                    parentPath={`${parentPath}.${item.fieldName}`}
                  />
                ))}
            </SortableContext>
          </TableBody>
        </Table>
      </DndContext>
    </Paper>
  );
};

const NestedGroup = memo(({ group, parentPath }) => {
  const formContext = useContext(FormContext);
  const [expanded, setExpanded] = useState(false);
  const [shouldRenderContent, setShouldRenderContent] = useState(false);

  const handleChange = (event, isExpanded) => {
    setExpanded(isExpanded);
    if (isExpanded) {
      // If opened, immediately render the content
      setShouldRenderContent(true);
    }
  };

  const handleTransitionExited = () => {
    // When the collapse animation is complete, delete the content
    // performance issue
    if (!expanded) {
      setShouldRenderContent(false);
    }
  };

  return (
    <Accordion
      variant="incident"
      expanded={expanded}
      onChange={handleChange}
      slotProps={{
        transition: {
          onExited: handleTransitionExited,
        },
      }}
    >
      <AccordionSummary variant="incident" expandIcon={<ExpandMoreIcon />}>
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Typography className="title">
            {capitalizeFirstLetter(group.label)}
          </Typography>
          <TooltipWrapper title={group.unchangeable && 'NERIS mandatory'}>
            <Switch
              size="small"
              disabled={group.unchangeable}
              checked={group.active}
              onChange={(_, value) => {
                formContext.onChange(`${parentPath}.active`, value);
              }}
              onClick={(e) => {
                // prevent opening accordion
                e.stopPropagation();
              }}
            />
          </TooltipWrapper>
        </Box>
      </AccordionSummary>

      <AccordionDetails
        variant="incident"
        sx={{
          margin: '0 16px 16px',
        }}
      >
        {shouldRenderContent && (
          <Stack spacing={2}>
            <Box
              sx={{
                maxWidth: '650px',
              }}
            >
              <FieldsList
                fieldList={group.fields}
                parentPath={`${parentPath}.fields`}
              />
            </Box>

            {checkIsObject(group.child) && (
              <NestedGroupList
                groupList={group.child}
                parentPath={`${parentPath}.child`}
              />
            )}
          </Stack>
        )}
      </AccordionDetails>
    </Accordion>
  );
});

const SortableNestedGroup = ({ group, parentPath, id }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    zIndex: isDragging ? 9999 : 'auto',
    position: isDragging ? 'relative' : 'static',
  };

  return (
    <Box ref={setNodeRef} style={style} {...attributes}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
        <Box
          {...listeners}
          sx={{
            alignItems: '',
            cursor: 'grab',
            '&:active': {
              cursor: 'grabbing',
            },
            mt: 2,
            mr: 1,
          }}
        >
          <IconButton size="small" sx={{ cursor: 'inherit !important' }}>
            <DragIndicatorIcon fontSize="small" />
          </IconButton>
        </Box>
        <Box sx={{ flex: 1 }}>
          <NestedGroup group={group} parentPath={parentPath} />
        </Box>
      </Box>
    </Box>
  );
};

export const NestedGroupList = ({ groupList, parentPath = '' }) => {
  const formContext = useContext(FormContext);
  const [items, setItems] = useState([]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Initialize items when groupList changes
  useEffect(() => {
    if (checkIsObject(groupList)) {
      const sortedItems = Object.entries(groupList)
        .sort(
          ([, fieldGroupA], [, fieldGroupB]) =>
            (fieldGroupA[0].order || 0) - (fieldGroupB[0].order || 0)
        )
        .map(([groupName, fieldGroup]) => ({
          id: groupName,
          groupName,
          fieldGroup,
        }));
      setItems(sortedItems);
    }
  }, [groupList]);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active?.id && over?.id && active.id !== over.id) {
      setItems((items) => {
        const oldIndex = items.findIndex((item) => item?.id === active.id);
        const newIndex = items.findIndex((item) => item?.id === over.id);

        if (oldIndex === -1 || newIndex === -1) return items;

        const newItems = arrayMove(items, oldIndex, newIndex);

        // Update order in form context
        formContext.onChange(
          newItems
            .filter((item) => item?.groupName)
            .map((item, index) => [
              `${parentPath}.${item.groupName}[0].order`,
              index,
            ])
        );

        // Update local order
        return newItems;
      });
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={items.filter((item) => item?.id).map((item) => item.id)}
        strategy={verticalListSortingStrategy}
      >
        <Stack spacing={2}>
          {items
            .filter(
              (item) => item?.id && item?.fieldGroup?.[0] && item?.groupName
            )
            .map((item) => (
              <SortableNestedGroup
                key={item.id}
                id={item.id}
                group={item.fieldGroup[0]}
                parentPath={`${parentPath}.${item.groupName}[0]`}
              />
            ))}
        </Stack>
      </SortableContext>
    </DndContext>
  );
};
